import { isNil, isPresent, keyBy, keys, mapKeys, pick, withRetryOnError } from "@mixitone/util";
import Debug from "debug";
import { SubscriptionManager, SubscriptionSource } from "./SubscriptionManager";
import { DatabaseAdapter } from "./adapters/DatabaseAdapter";
import { Collection, CollectionDescriptor } from "./collection";
import { AnyModelConfig, ModelCollections, ModelFields, ModelFieldsWithId, ModelReferences } from "./config";
import { NotFoundError } from "./errors";
import { oom } from "./index";
import { GetModelConfig, Model, ModelConstructor } from "./model";
import { ModelRegistry } from "./modelRegistry";
import { ReferenceDescriptor, References } from "./reference";
import requestQueue from "./requestQueue";

const debug = Debug("oom:query");

function mergeUnique<T>(existing: T[] | undefined, items: T[]): T[] | undefined {
  if (items.length === 0) return existing;
  const merged = [...(existing ?? [])];
  for (const item of items) {
    if (!merged.includes(item)) {
      merged.push(item);
    }
  }
  return merged;
}

/** PostgREST may reject a brand-new JWT as "future" if service clocks differ by a sub-second margin. */
const POSTGREST_JWT_FUTURE_MAX_ATTEMPTS = 4;
const POSTGREST_JWT_FUTURE_BASE_DELAY_MS = 300;

function isPostgrestJwtFutureClockSkewError(error: { message?: string } | null | undefined): boolean {
  const msg = error?.message ?? "";
  return msg.includes("JWT issued at future") || msg.includes("PGRST303");
}

function shouldRetryPostgrestJwtClockSkew<T extends { error?: { message?: string } | null }>(
  result: T,
): boolean {
  const err = result.error ?? undefined;
  return Boolean(err && isPostgrestJwtFutureClockSkewError(err));
}

interface SingleOrder<F extends AnyModelConfig> {
  column: string & keyof ModelFields<F>;
  direction: "asc" | "desc";
}

type Order<F extends AnyModelConfig> = SingleOrder<F> | SingleOrder<F>[];

export type BinaryOperator = "eq" | "gt" | "gte" | "lt" | "lte" | "neq";
export type ArrayOperator = "in";
export type NullOperator = "is_null" | "is_not_null";
export type Operator = BinaryOperator | ArrayOperator | NullOperator;

interface BinaryOperatorValue<T> {
  op: BinaryOperator;
  value: T;
}
interface ArrayOperatorValue<T> {
  op: ArrayOperator;
  value: T[];
}
interface NullOperatorValue {
  op: NullOperator;
}
export type QueryFilterValue<T> = BinaryOperatorValue<T> | ArrayOperatorValue<T> | NullOperatorValue;

export type QueryFilter<F extends AnyModelConfig> = {
  [P in keyof ModelFields<F> & string]?: QueryFilterValue<ModelFields<F>[P]>;
};

type FilterValue<T> = T extends Date ? T | "today" : T;
type FilterValueFor<C extends AnyModelConfig, K extends keyof ModelFieldsWithId<C> & string> = FilterValue<
  ModelFields<C>[K]
>;

function normalizeFilterValue(
  value: undefined | null | any[] | any,
  fieldType: AnyModelConfig["fields"][string]["type"] | undefined,
) {
  const valueConverters: Array<{
    matches: (item: any, type: AnyModelConfig["fields"][string]["type"] | undefined) => boolean;
    convert: (item: any) => any;
  }> = [
    {
      matches: (item, type) => type === "datetime" && item instanceof Date,
      convert: (item) => item.toISOString(),
    },
  ];

  const normalizeSingleValue = (item: any) => {
    const converter = valueConverters.find((candidate) => candidate.matches(item, fieldType));
    return converter ? converter.convert(item) : item;
  };

  if (Array.isArray(value)) return value.map(normalizeSingleValue);
  return normalizeSingleValue(value);
}

export type SubscriptionOptions<M extends Model<any>, C extends AnyModelConfig> = {
  filter?: Partial<ModelFields<C>>;
  callback: SubscriptionCallback<M>;
  source?: SubscriptionSource | SubscriptionSource[];
  ignoreSelfBroadcast?: boolean;
};
interface SubscriptionCallbackInsert<M extends Model<any>> {
  type: "INSERT";
  model: M;
  source: SubscriptionSource;
}
interface SubscriptionCallbackUpdate<M extends Model<any>> {
  type: "UPDATE";
  model: M;
  source: SubscriptionSource;
  changes: Partial<ModelFields<GetModelConfig<M>>>;
}
interface SubscriptionCallbackDelete<M extends Model<any>> {
  type: "DELETE";
  model: M;
  source: SubscriptionSource;
}
export type SubscriptionCallbackPayload<M extends Model<any>> =
  | SubscriptionCallbackInsert<M>
  | SubscriptionCallbackUpdate<M>
  | SubscriptionCallbackDelete<M>;

export type SubscriptionCallback<M extends Model<any>> = (
  payload: Array<SubscriptionCallbackPayload<M>>,
) => void;

interface FindOptions {
  noCache?: boolean;
}

interface BaseQueryOptions {
  adapter: DatabaseAdapter;
}

interface QueryOptions<C extends AnyModelConfig> {
  filter?: QueryFilter<C>;
  order?: Order<C>;
  limit?: number;
  preloadCounts?: string[];
  preloadCollections?: Array<keyof ModelCollections<C>>;
  preloadReferences?: Array<keyof ModelReferences<C> & string>;
  from?: string;
  selection?: string[];
  joins?: Query<any, any>[];
  includeDeleted?: boolean;
  adapter: DatabaseAdapter;
}

export interface SelectableQuery {
  get selection(): string[];
}

export interface WhereableQuery<C extends AnyModelConfig> {
  get filter(): QueryFilter<C>;

  eq<K extends keyof ModelFieldsWithId<C> & string>(
    field: K,
    value: undefined | null | (K extends "id" ? string : ModelFields<C>[K]),
  ): WhereableQuery<C>;
  neq<K extends keyof ModelFieldsWithId<C> & string>(
    field: K,
    value: undefined | null | (K extends "id" ? string : ModelFields<C>[K]),
  ): WhereableQuery<C>;
  gt<K extends keyof ModelFieldsWithId<C> & string>(field: K, value: FilterValueFor<C, K>): WhereableQuery<C>;
  gte<K extends keyof ModelFieldsWithId<C> & string>(
    field: K,
    value: FilterValueFor<C, K>,
  ): WhereableQuery<C>;
  lt<K extends keyof ModelFieldsWithId<C> & string>(field: K, value: FilterValueFor<C, K>): WhereableQuery<C>;
  lte<K extends keyof ModelFieldsWithId<C> & string>(
    field: K,
    value: FilterValueFor<C, K>,
  ): WhereableQuery<C>;
  in<K extends (keyof ModelFields<C> & string) | "id">(
    field: K,
    value: Array<K extends "id" ? string : ModelFields<C>[K]>,
  ): WhereableQuery<C>;
  is_null<K extends keyof ModelFieldsWithId<C> & string>(field: K): WhereableQuery<C>;
  is_not_null<K extends keyof ModelFieldsWithId<C> & string>(field: K): WhereableQuery<C>;
}

export class BaseQuery<M extends Model<any>, C extends GetModelConfig<M>> {
  config: C;

  constructor(
    public modelClass: ModelConstructor<M>,
    public options: BaseQueryOptions,
  ) {
    this.config = modelClass.config;
  }

  get adapter() {
    return this.options.adapter;
  }

  get tableName() {
    return this.modelClass.tableName;
  }
}

export class Query<M extends Model<any>, C extends GetModelConfig<M>>
  extends BaseQuery<M, C>
  implements SelectableQuery, WhereableQuery<C>
{
  constructor(
    modelClass: ModelConstructor<M>,
    public options: QueryOptions<C>,
  ) {
    super(modelClass, options);

    if (isNil(this.options.selection)) {
      this.options.selection = keys(this.config.fields) as string[];
    }
  }

  get filter(): QueryFilter<C> {
    return this.options.filter || {};
  }

  get order() {
    return this.options.order;
  }

  get preloadCollections() {
    return this.options.preloadCollections;
  }

  get preloadReferences() {
    return this.options.preloadReferences;
  }

  get hasPreloads() {
    return this.preloadCollections || this.preloadReferences || this.options.preloadCounts;
  }

  get tableName() {
    return this.options.from || super.tableName;
  }

  get selection() {
    const selection = this.options.selection || [];
    selection.push("id");
    return selection;
  }

  get joinQueries() {
    return this.options.joins || [];
  }

  get joinsFilter() {
    return this.joinQueries.reduce((acc, join) => {
      const joinFilter = mapKeys(join.filter, (k) => `${join.tableName}.${k}`);
      return { ...acc, ...joinFilter };
    }, {});
  }

  /**
   * Returns true if the given model would match this query
   */
  matches(model: M) {
    if (model.rawAttributes["is_deleted"] === true) return false;

    return Object.entries(this.filter).every(([key, value]) => {
      if (!value) return true;

      switch (value.op) {
        case "eq":
          return model.readAttribute(key) === value.value;
        case "neq":
          return model.readAttribute(key) !== value.value;
        case "gt":
          return model.readAttribute(key) > value.value;
        case "gte":
          return model.readAttribute(key) >= value.value;
        case "lt":
          return model.readAttribute(key) < value.value;
        case "lte":
          return model.readAttribute(key) <= value.value;
        case "in":
          return value.value.includes(model.readAttribute(key));
        case "is_not_null":
          return isPresent(model.readAttribute(key));
        case "is_null":
          return isNil(model.readAttribute(key));
      }
    });
  }

  static inFlightRequests = new Map<string, WeakRef<Promise<any>>>();

  /**
   * Execute the query filtering to a single record by id
   *
   * @param id
   * @param options
   */
  async find(id: string, options: FindOptions = {}): Promise<M> {
    if (!id) throw new Error("ID is required to find a record");

    const requestKey = ModelRegistry.instance.keyFor(this.tableName, id);
    let model;

    if (!options.noCache) {
      const inFlight = Query.inFlightRequests.get(requestKey);
      if (inFlight) {
        const promise = inFlight.deref();
        if (promise) await promise;
      }

      model = ModelRegistry.instance.get(this.modelClass, id);
      if (model) await this.loadPreloads([model]);
    }

    if (!model) {
      const request = async () => {
        const response = await requestQueue.add(() =>
          withRetryOnError(() => this.adapter.execute(this.eq("id", id).limit(1)), {
            maxAttempts: POSTGREST_JWT_FUTURE_MAX_ATTEMPTS,
            baseDelayMs: POSTGREST_JWT_FUTURE_BASE_DELAY_MS,
            shouldRetry: shouldRetryPostgrestJwtClockSkew,
          }),
        );
        if (!response) throw new NotFoundError();
        if (response.error || !response.data || response.data.length < 1) {
          console.error("Error finding model using query", this.tableName, id, this, response);
          throw new NotFoundError(response.error?.message || "Not found");
        }
        const model = new this.modelClass(response.data[0] as any);
        await this.loadPreloads([model], options.noCache);
        return model;
      };

      const promise = request();
      Query.inFlightRequests.set(requestKey, new WeakRef(promise));
      model = await promise.finally(() => Query.inFlightRequests.delete(requestKey));
    }

    return model;
  }

  /**
   * Find a single record with the given criteria. This is the same as running `.eq(field, value).first()`
   *
   * @param field
   * @param value
   * @returns
   */
  async findBy<K extends keyof ModelFields<C> | "id">(
    field: K,
    value: ModelFields<C>[K],
  ): Promise<undefined | M> {
    if (typeof field !== "string") throw new Error("not a string");

    return this.eq(field, value).first();
  }

  /**
   * Execute the query with a limit of 1 and return the first record
   */
  async first(): Promise<undefined | M> {
    const limitQuery = this.limit(1);
    const models = await limitQuery.all();

    if (models.length === 0) return;
    return models[0];
  }
  /**
   * Perform a count operation
   */
  async count(): Promise<number> {
    const response = await withRetryOnError(() => this.adapter.count(this), {
      maxAttempts: POSTGREST_JWT_FUTURE_MAX_ATTEMPTS,
      baseDelayMs: POSTGREST_JWT_FUTURE_BASE_DELAY_MS,
      shouldRetry: shouldRetryPostgrestJwtClockSkew,
    });

    if (response.error) throw new Error(response.error.message);
    if (!response.count) return 0;

    return response.count;
  }
  /**
   * Return all records matching the query
   */
  async all(): Promise<M[]> {
    const response = await requestQueue.add(() =>
      withRetryOnError(() => this.adapter.execute(this), {
        maxAttempts: POSTGREST_JWT_FUTURE_MAX_ATTEMPTS,
        baseDelayMs: POSTGREST_JWT_FUTURE_BASE_DELAY_MS,
        shouldRetry: shouldRetryPostgrestJwtClockSkew,
      }),
    );
    if (!response || response.error) {
      throw new Error(response?.error?.message || "Error executing query");
    }

    const models = response.data ? response.data.map((row: any) => new this.modelClass(row)) : [];

    await this.loadPreloads(models);
    return models;
  }

  /**
   * Return just the specific field data for a query
   */
  async pluck<K extends keyof ModelFieldsWithId<C>>(field: K & string): Promise<ModelFieldsWithId<C>[K][]> {
    const query = this.reselect(field);
    const response = await requestQueue.add(() =>
      withRetryOnError(() => this.adapter.execute<Record<K, any>>(query), {
        maxAttempts: POSTGREST_JWT_FUTURE_MAX_ATTEMPTS,
        baseDelayMs: POSTGREST_JWT_FUTURE_BASE_DELAY_MS,
        shouldRetry: shouldRetryPostgrestJwtClockSkew,
      }),
    );

    if (!response) throw new NotFoundError();
    if (response.error) throw new Error(response.error.message);
    if (!response.data) return [];

    return response.data.map((row: any) => row[field]);
  }

  async loadPreloads(models: M[], noCache?: boolean): Promise<void> {
    await Promise.all([
      this.loadReferences(models, noCache),
      this.loadCollections(models, noCache),
      this.loadCounts(models),
    ]);
  }

  async loadReferences<M extends Model<any>>(models: M[], noCache?: boolean): Promise<void> {
    if (!this.preloadReferences) return;

    for (const reference of this.preloadReferences) {
      const referenceConfig = this.config.references[reference] as ReferenceDescriptor<any>;

      const idMap = models.reduce<Record<string, M[]>>((acc, model) => {
        if (noCache || !model.readReference(reference as string)?.loaded) {
          const id = model.readAttribute(referenceConfig.key);
          if (id) {
            acc[id] = acc[id] ? [...acc[id], model] : [model];
          }
        }
        return acc;
      }, {});

      const ids = keys(idMap);
      if (ids.length === 0) continue;

      const referenceModels = await referenceConfig.model().query().in("id", ids).all();

      referenceModels.forEach((refModel) => {
        idMap[refModel.id]?.forEach((model) => model.readReference(reference as string)?.set(refModel));
      });
    }
  }

  /**
   * Load all the given collections on a set of models
   */
  async loadCollections(models: M[], noCache?: boolean): Promise<void> {
    if (!this.preloadCollections) return;

    const collectionLoads = this.preloadCollections.map((collection) =>
      this.loadCollection(models, collection, noCache),
    );
    await Promise.all(collectionLoads);
  }

  async loadCounts(models: M[]): Promise<void> {
    if (!this.options.preloadCounts) return;

    const countLoads = this.options.preloadCounts.map((collection) => this.loadCount(models, collection));
    await Promise.all(countLoads);
  }

  async loadCount<N extends keyof C["collections"]>(models: M[], collectionName: N): Promise<void> {
    const collectionConfig = this.config.collections[collectionName] as CollectionDescriptor<any>;
    if (!collectionConfig) throw new Error("Collection not found");

    const key = collectionConfig.key;
    const query = collectionConfig.model().query();

    for (const model of models) {
      let collectionData = model.data.get(collectionName as string) || [];
      if (isNil(collectionData[0])) {
        const count = await query.eq(key, model.id).count();
        collectionData[0] = { count };
        model.data.set(collectionName as string, collectionData);
      }
    }
  }

  /**
   * Load the given collection on a set of models
   */
  async loadCollection<N extends keyof C["collections"]>(
    models: M[],
    collectionName: N,
    noCache?: boolean,
  ): Promise<void> {
    const collectionConfig = this.config.collections[collectionName] as CollectionDescriptor<any>;
    if (!collectionConfig) throw new Error("Collection not found");

    // Filter out models that already have the collection loaded
    const modelsNeedingLoading = models.filter(
      (model) => noCache || !model.readCollection(collectionName as string)?.loaded,
    );
    // Nothing needs loading
    if (modelsNeedingLoading.length === 0) return;

    const modelIds = modelsNeedingLoading.map((model) => model.id!);
    const key = collectionConfig.key;
    const collectionModel = collectionConfig.model();
    const query = collectionModel
      .query()
      .orderBy(collectionConfig.order || "id")
      .in(key, modelIds);

    const collectionModels = await query.all();

    debug("collection load", collectionModels.length, "models for", collectionName);

    const collectionReverseReference = Object.entries(collectionModel.config.references as References).find(
      ([_, ref]) => ref.model() === this.modelClass && ref.key === collectionConfig.key,
    );

    for (const model of modelsNeedingLoading) {
      const collectionDataForModel = collectionModels.filter((m) => m[key] === model.id);
      const collection = model.readCollection(collectionName as string) as Collection<any>;
      collection.set(collectionDataForModel);

      if (collectionReverseReference) {
        collectionDataForModel.forEach((m) => {
          m.readReference(collectionReverseReference[0])?.set(model);
        });
      }
    }
  }

  /**
   * Create a subscription for changes for records that match this query
   */
  async subscribe(options: SubscriptionOptions<M, C>): Promise<() => void> {
    const { filter, callback, source, ignoreSelfBroadcast } = options;

    return await SubscriptionManager.instance.add(this.modelClass.tableName, {
      filter: filter ?? {},
      ignoreSelfBroadcast,
      callback: async (data, eventSource) => {
        const idsToReload: string[] = data
          .filter((d) => {
            return (
              (d.eventType === "UPDATE" || d.eventType === "INSERT") &&
              Object.keys(d.new).length === 1 &&
              "id" in d.new
            );
          })
          .map((d) => ("id" in d.new ? d.new.id : ""));

        const reloadedModels =
          idsToReload.length === 0
            ? {}
            : keyBy(await this.modelClass.query().in("id", idsToReload).all(), "id");

        const transformedData: SubscriptionCallbackPayload<M>[] = [];
        for (const d of data) {
          const attributes = d.eventType === "DELETE" ? d.old : (d.new as any);
          if (!attributes) {
            oom.captureException(new Error("No attributes found in event"), {
              table: d.table,
              source: eventSource,
            });
            continue;
          }
          let type = d.eventType;
          if (type === "UPDATE" && "is_deleted" in d.new && d.new.is_deleted) {
            // If the record is marked as deleted, treat it as a delete event
            type = "DELETE";
          }

          let model = attributes.id ? reloadedModels[attributes.id] : undefined;
          if (!model && attributes.id && (type === "UPDATE" || type === "INSERT")) {
            try {
              model = await this.modelClass.query().find(attributes.id, { noCache: true });
            } catch {
              model = undefined;
            }
          }
          if (!model) {
            if (!attributes.id) continue;
            model = new this.modelClass(attributes, eventSource);
          }

          switch (type) {
            case "INSERT":
              transformedData.push({
                type: "INSERT",
                model,
                source: eventSource,
              } satisfies SubscriptionCallbackInsert<M>);
              break;
            case "UPDATE":
              transformedData.push({
                type: "UPDATE",
                model,
                source: eventSource,
                changes: d.old,
              } satisfies SubscriptionCallbackUpdate<M>);
              break;
            case "DELETE":
              transformedData.push({
                type: "DELETE",
                model,
                source: eventSource,
              } satisfies SubscriptionCallbackDelete<M>);
              break;
          }
        }

        if (transformedData.length === 0) return;
        callback(transformedData);
      },
      source,
    });
  }

  /**
   * When this query executes it will preload the given collections
   */
  preload<K extends keyof (ModelCollections<C> & ModelReferences<C>)>(...preloads: K[]): Query<M, C> {
    const collections = preloads.filter((p) => p in this.modelClass.config.collections);
    const references = preloads.filter((p) => p in this.modelClass.config.references);

    return new Query<M, C>(this.modelClass, {
      ...this.options,
      preloadCollections: mergeUnique(this.options.preloadCollections, collections as any) as any,
      preloadReferences: mergeUnique(this.options.preloadReferences, references as any) as any,
    });
  }

  /**
   * When this query executes it will preload the given collection counts
   */
  preloadCounts<K extends keyof ModelCollections<C>>(...collections: K[]): Query<M, C> {
    return new Query<M, C>(this.modelClass, {
      ...this.options,
      preloadCounts: mergeUnique(this.options.preloadCounts, collections as any) as any,
    });
  }

  /**
   * Apply an order to the query
   *
   * @example
   * // Single field ordering
   * query.orderBy("name", "asc")
   * query.orderBy("created_at", "desc")
   *
   * @example
   * // Multiple field ordering with same direction
   * query.orderBy(["name", "created_at"], "asc")
   * query.orderBy(["priority", "name"], "desc")
   *
   * @example
   * // Multiple field ordering with different directions
   * query.orderBy(["priority", "name"], ["desc", "asc"])
   */
  orderBy<K extends keyof ModelFields<C> & string>(
    field: K | K[],
    direction?: "asc" | "desc" | ("asc" | "desc")[],
  ): Query<M, C> {
    if (typeof field === "string") {
      // Single field ordering
      const singleDirection = Array.isArray(direction) ? direction[0] : direction || "asc";
      return new Query(this.modelClass, {
        ...this.options,
        order: { column: field, direction: singleDirection },
      });
    } else if (Array.isArray(field)) {
      // Multiple field ordering
      const orders: SingleOrder<C>[] = field.map((f, index) => {
        const dir = Array.isArray(direction) ? direction[index] || "asc" : direction || "asc";
        return { column: f, direction: dir };
      });
      return new Query(this.modelClass, {
        ...this.options,
        order: orders,
      });
    } else {
      throw new Error("field must be a string or array of strings");
    }
  }

  // Generic method to handle setting up filter criteria
  private addFilter<K extends keyof ModelFieldsWithId<C> & string>(
    field: K,
    value:
      | undefined
      | null
      | (K extends "id" ? string : ModelFields<C>[K])
      | (K extends "id" ? string : ModelFields<C>[K])[],
    op: BinaryOperator | "in" | NullOperator,
  ): Query<M, C> {
    if (typeof field !== "string") throw new Error("not a string");
    const fieldType = this.config.fields[field]?.type;
    const normalizedValue = normalizeFilterValue(value, fieldType);

    return new Query(this.modelClass, {
      ...this.options,
      filter: {
        ...this.filter,
        [field]: { op, value: normalizedValue },
      },
    });
  }

  // Refactored methods using the generic addFilter method
  eq<K extends keyof ModelFieldsWithId<C> & string>(
    field: K,
    value: undefined | null | (K extends "id" ? string : FilterValueFor<C, K>),
  ): Query<M, C> {
    return this.addFilter(field, value, "eq");
  }
  neq<K extends keyof ModelFieldsWithId<C> & string>(
    field: K,
    value: undefined | null | (K extends "id" ? string : FilterValueFor<C, K>),
  ): Query<M, C> {
    return this.addFilter(field, value, "neq");
  }
  gt<K extends keyof ModelFieldsWithId<C> & string>(field: K, value: FilterValueFor<C, K>): Query<M, C> {
    return this.addFilter(field, value, "gt");
  }
  gte<K extends keyof ModelFieldsWithId<C> & string>(field: K, value: FilterValueFor<C, K>): Query<M, C> {
    return this.addFilter(field, value, "gte");
  }
  lt<K extends keyof ModelFieldsWithId<C> & string>(field: K, value: FilterValueFor<C, K>): Query<M, C> {
    return this.addFilter(field, value, "lt");
  }
  lte<K extends keyof ModelFieldsWithId<C> & string>(field: K, value: FilterValueFor<C, K>): Query<M, C> {
    return this.addFilter(field, value, "lte");
  }
  in<K extends (keyof ModelFields<C> & string) | "id">(
    field: K,
    value: Array<K extends "id" ? string : FilterValueFor<C, K>>,
  ) {
    if (!Array.isArray(value)) throw new Error("value must be an array");
    return this.addFilter(field, value, "in");
  }

  is_null<K extends keyof ModelFieldsWithId<C> & string>(field: K): Query<M, C> {
    return this.addFilter(field, null, "is_null");
  }

  is_not_null<K extends keyof ModelFieldsWithId<C> & string>(field: K): Query<M, C> {
    return this.addFilter(field, null, "is_not_null");
  }

  /**
   * Limit the number of records returned by the query
   */
  limit(limit: number | undefined) {
    return new Query(this.modelClass, {
      ...this.options,
      limit,
    });
  }

  /**
   * Change the table or view that the query is running against
   */
  from(tableOrViewName: string) {
    return new Query(this.modelClass, {
      ...this.options,
      from: tableOrViewName,
    });
  }

  /**
   * Add additional fields to the selection
   */
  select(...selection: string[]) {
    return this.reselect(...(this.options.selection || []), ...selection);
  }

  /**
   * Change the selection to the given fields
   */
  reselect(...selection: string[]) {
    return new Query(this.modelClass, {
      ...this.options,
      selection,
    });
  }

  joins(query: Query<any, any>) {
    return new Query(this.modelClass, {
      ...this.options,
      joins: [...(this.options.joins || []), query],
    });
  }

  includeDeleted() {
    return new Query(this.modelClass, {
      ...this.options,
      includeDeleted: true,
    });
  }

  async updateAll(attributes: Partial<ModelFields<C>>): Promise<void> {
    const updateQuery = new UpdateQuery(this.modelClass, {
      adapter: this.adapter,
      filter: this.filter,
      selection: ["*"],
    });
    await updateQuery.set(attributes);
  }

  async deleteAll(): Promise<void> {
    const deleteQuery = new UpdateQuery(this.modelClass, {
      adapter: this.adapter,
      filter: this.filter,
      selection: ["*"],
    });
    await deleteQuery.delete();
  }
}

interface InsertQueryOptions {
  adapter: DatabaseAdapter;
  selection?: string[];
}

export class InsertQuery<M extends Model<any>, C extends GetModelConfig<M>>
  extends BaseQuery<M, C>
  implements SelectableQuery
{
  constructor(
    modelClass: ModelConstructor<M>,
    public options: InsertQueryOptions,
  ) {
    super(modelClass, options);

    if (isNil(this.options.selection)) {
      this.options.selection = keys(this.config.fields) as string[];
    }
  }

  get selection() {
    return this.options.selection || [];
  }

  async insert(attributes: Partial<ModelFields<C>>): Promise<Partial<M["rawAttributes"]>> {
    const response = await this.adapter.insert<any>(this, attributes);
    if (response.error) {
      throw new Error(response.error.message);
    }
    return response.data as Partial<M["rawAttributes"]>;
  }

  /**
   * Change the selection to the given fields
   */
  select(...selection: string[]) {
    return new InsertQuery(this.modelClass, {
      ...this.options,
      selection,
    });
  }
}
interface UpdateQueryOptions<C extends AnyModelConfig> {
  filter?: QueryFilter<C>;
  adapter: DatabaseAdapter;
  selection?: string[];
  single?: boolean;
}

export class UpdateQuery<M extends Model<any>, C extends GetModelConfig<M>>
  extends BaseQuery<M, C>
  implements SelectableQuery, WhereableQuery<C>
{
  constructor(
    modelClass: ModelConstructor<M>,
    public options: UpdateQueryOptions<C>,
  ) {
    super(modelClass, options);
  }

  get filter(): QueryFilter<C> {
    return this.options.filter || {};
  }

  get selection() {
    return this.options.selection || [];
  }

  async set(attributes: Partial<ModelFields<C>>) {
    const response = await this.adapter.update(this, attributes);
    if (response.error) {
      throw new Error(response.error.message);
    }
  }

  async delete() {
    const response = await this.adapter.delete(this);
    if (response.error) {
      throw new Error(response.error.message);
    }
  }

  /**
   * Change the selection to the given fields
   */
  select(...selection: string[]) {
    return new UpdateQuery(this.modelClass, {
      ...this.options,
      selection,
    });
  }

  // Generic method to handle setting up filter criteria
  private addFilter<K extends keyof ModelFieldsWithId<C> & string>(
    field: K,
    value:
      | undefined
      | null
      | (K extends "id" ? string : ModelFields<C>[K])
      | (K extends "id" ? string : ModelFields<C>[K])[],
    op: BinaryOperator | "in" | NullOperator,
  ): UpdateQuery<M, C> {
    if (typeof field !== "string") throw new Error("not a string");
    const fieldType = this.config.fields[field]?.type;
    const normalizedValue = normalizeFilterValue(value, fieldType);

    return new UpdateQuery(this.modelClass, {
      ...this.options,
      filter: {
        ...this.filter,
        [field]: { op, value: normalizedValue },
      },
    });
  }

  // Refactored methods using the generic addFilter method
  eq<K extends keyof ModelFieldsWithId<C> & string>(
    field: K,
    value: undefined | null | (K extends "id" ? string : FilterValueFor<C, K>),
  ) {
    return this.addFilter(field, value, "eq");
  }
  neq<K extends keyof ModelFieldsWithId<C> & string>(
    field: K,
    value: undefined | null | (K extends "id" ? string : FilterValueFor<C, K>),
  ) {
    return this.addFilter(field, value, "neq");
  }
  gt<K extends keyof ModelFieldsWithId<C> & string>(field: K, value: FilterValueFor<C, K>) {
    return this.addFilter(field, value, "gt");
  }
  gte<K extends keyof ModelFieldsWithId<C> & string>(field: K, value: FilterValueFor<C, K>) {
    return this.addFilter(field, value, "gte");
  }
  lt<K extends keyof ModelFieldsWithId<C> & string>(field: K, value: FilterValueFor<C, K>) {
    return this.addFilter(field, value, "lt");
  }
  lte<K extends keyof ModelFieldsWithId<C> & string>(field: K, value: FilterValueFor<C, K>) {
    return this.addFilter(field, value, "lte");
  }
  in<K extends (keyof ModelFields<C> & string) | "id">(
    field: K,
    value: Array<K extends "id" ? string : FilterValueFor<C, K>>,
  ) {
    if (!Array.isArray(value)) throw new Error("value must be an array");
    return this.addFilter(field, value, "in");
  }

  is_null<K extends keyof ModelFieldsWithId<C> & string>(field: K): UpdateQuery<M, C> {
    return this.addFilter(field, null, "is_null");
  }

  is_not_null<K extends keyof ModelFieldsWithId<C> & string>(field: K): UpdateQuery<M, C> {
    return this.addFilter(field, null, "is_not_null");
  }
}
